import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map, Observable } from 'rxjs';
import { IAddress } from '../models/profile.model';
import { IRpcRequest, IRpcResponse, OrganizationInfoResult, } from '../models/rpc-response.model';
import { environment } from 'src/environments/environment';
import { IUserToUpdate } from '../models/user.model';
import { IResultSendingOrder, orderStatuses } from '../models/order-info.model';
import { AuthResponse, OrderResponse, UserAddressResponse, UserResponse } from '../models/api-responses';
import { Organization } from '../models/organization.model';
import { IDeliveryTerminalInfo } from '../models/organizationInfo/deliveryTerminal/deliveryTerminal.model';
import { ITerminalPaymentType } from '../models/organizationInfo/deliveryTerminal/terminalPaymentTypes.model';
import {
  CreateOrderRequest,
  OrderProductDetails,
  OrderProductModifierDetails,
  SmsSendResponse,
  UserAddressUpdateRequest
} from '../models/api.models';
import { IOrderDiscountInfo } from '../models/order-discount-info.model';
import { ConverterService } from './converter.service';
import { UserOrder } from '../models/user-order.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  baseUrl = '/api';
  private headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  constructor(
    private http: HttpClient,
    private converterService: ConverterService
  ) {
    this.baseUrl = environment.url + this.baseUrl;
  }

  organizationInfoGetByCode(code: string): Observable<IRpcResponse<OrganizationInfoResult>> {
    return this.rpcCall(`${this.baseUrl}/rpc/organizationInfo`, 'GetByCode', {
      organizationCode: code
    });
  }

  sms(phone: string, organizationId: string): Observable<IRpcResponse<SmsSendResponse>> {
    return this.rpcCall(`${this.baseUrl}/rpc/sms`, 'Send', {
      organizationId,
      phone
    });
  }

  auth(organizationId: string, verificationCode: number, phone: number): Observable<IRpcResponse<AuthResponse>> {
    return this.rpcCall(`${this.baseUrl}/rpc/auth`, 'ConfirmVerificationCode', {
      organizationId,
      phone: phone.toString(),
      verificationCode
    });
  }

  // null реально можно получить
  userGet(): Observable<IRpcResponse<UserResponse | null>> {
    return this.rpcCall(`${this.baseUrl}/rpc/user`, 'get');
  }

  userUpdate(userInfo: IUserToUpdate): Observable<IRpcResponse<UserResponse>> {
    return this.rpcCall(`${this.baseUrl}/rpc/user`, 'update', {
      updateOrganizationUser: userInfo
    });
  }

  addAddress(userAddress: IAddress): Observable<IRpcResponse<UserAddressResponse>> {
    return this.rpcCall(`${this.baseUrl}/rpc/useraddresses`, 'add', {
      userAddress
    });
  }

  updateAddress(userAddress: UserAddressUpdateRequest): Observable<IRpcResponse<UserAddressResponse[]>> {
    return this.rpcCall(`${this.baseUrl}/rpc/useraddresses`, 'update', {
      userAddress
    });
  }

  deleteAddress(userAddressId: string): Observable<any> {
    return this.rpcCall<IRpcResponse<any>>(`${this.baseUrl}/rpc/useraddresses`, 'delete', {
      userAddressId
    });
  }

  getOrders(): Observable<IRpcResponse<OrderResponse[]>> {
    return this.rpcCall(`${this.baseUrl}/rpc/order`, 'get');
  }

  sendOrder(
    createOrderRequest: CreateOrderRequest
  ): Observable<IRpcResponse<IResultSendingOrder>> {
    return this.rpcCall<IRpcResponse<IResultSendingOrder>>(`${this.baseUrl}/rpc/order`, 'sendOrder', createOrderRequest);
  }

  getDiscount(
    createOrderRequest: CreateOrderRequest
  ): Observable<IRpcResponse<IOrderDiscountInfo>> {
    return this.rpcCall<IRpcResponse<IOrderDiscountInfo>>(`${this.baseUrl}/rpc/order`, 'getDiscountOrder', createOrderRequest);
  }

  getOrderInfo(orderId: string): Observable<UserOrder> {
    return this.rpcCall<IRpcResponse<OrderResponse>>(`${this.baseUrl}/rpc/order`, 'Info', {
      orderId
    }).pipe(
      map(response => {
        return this.convertOrderToUserOrder(response.result);
      })
    );
  }

  getNewPaymentLink(orderId: string): Observable<IRpcResponse<string>> {
    return this.rpcCall<IRpcResponse<string>>(`${this.baseUrl}/rpc/order`, 'GetNewPaymentLink', {
      orderId
    });
  }

  getTerminalAddresses(organizationName: string): Observable<any> {
    return this.rpcCall<IRpcResponse<any>>(`${this.baseUrl}/rpc/organizationInfo`, 'GetByCode', {
      organizationCode: organizationName
    });
  }

  public convertOrderToUserOrder(order: OrderResponse): UserOrder {
    return {
      userAddress: order.userAddress,
      payment: order.payment,
      deliveryTerminal: order.deliveryTerminal,
      comment: order.comment,
      estimatedDeliveryTime: order.estimatedDeliveryTime,
      deliveryDate: order.deliveryDate,
      id: order.id,
      createdDate: order.createdDate,
      deliveryType: order.deliveryType,
      orderShortId: order.orderShortId,
      metaData: order.metaData,
      orderProducts: order.orderProducts.map<OrderProductDetails>(value => {
        return {
          product: {
            id: value.product.id,
            name: value.product.name,
            tags: value.product.tags,
            kind: value.product.kind,
            description: value.product.description,
            imageLinks: value.product.imageLinks,
            productGroupInfo: value.product.productGroupInfo
          },
          sort: value.sort,
          id: value.id,
          price: value.price,
          selectedQuantity: value.quantity,
          positionId: value.positionId ?? value.id,
          selectedSizeId: value.size?.id,
          modifiers: value.modifiers.map<OrderProductModifierDetails>(modifier => {
            return {
              id: modifier.id,
              name: modifier.name,
              calculatedPrice: modifier.price,
              selectedQuantity: modifier.quantity,
              productGroupInfoId: modifier.productGroupInfoId,
              positionId: modifier.positionId ?? modifier.id
            }
          })
        }
      }),
      passedSteps: ConverterService.convertUserOrderStatusToPassedSteps(order.orderStatus, order.deliveryType),
      status: orderStatuses[order.orderStatus]
    };
  }

  public convertOrganizationResponseToOrganization(organizationInfo: OrganizationInfoResult): Organization {
    let isSupportDelivery = false;
    let isSupportTakeaway = false;
    const deliveryTerminals = organizationInfo.deliveryTerminals && organizationInfo.deliveryTerminals.length
      ? organizationInfo.deliveryTerminals.map(
        deliveryTerminalResult => this.convertDeliveryTerminalResultToDeliveryTerminal(deliveryTerminalResult)
      ) : [];
    deliveryTerminals.forEach(deliveryTerminal => {
      isSupportDelivery = isSupportDelivery || deliveryTerminal.isSupportDelivery;
      isSupportTakeaway = isSupportTakeaway || deliveryTerminal.isSupportTakeaway;
    });

    const deliveryTerminalsWithZone = deliveryTerminals.filter(x => x.deliveryZones.length > 0);
    let isSupportDeliveryZones = deliveryTerminalsWithZone.length > 0;

    return {
      id: organizationInfo.id,
      name: organizationInfo.name,
      code: organizationInfo.code,
      phone: organizationInfo.phone,
      contacts: organizationInfo.contacts,
      deliveryTerminals,
      logo: organizationInfo.logo,
      mainColor: organizationInfo.mainColor,
      secondColor: organizationInfo.secondColor,
      thirdColor: organizationInfo.thirdColor,
      currency: organizationInfo.currency,
      externalId: organizationInfo.externalId,
      menu: {
        productGroups: organizationInfo.menu.productGroups.map(
          productGroupsResponse => ConverterService.convertProductGroupResponse(productGroupsResponse, organizationInfo.menu.sizes, organizationInfo.menu.products),
        ),
        productGroupsInfo: organizationInfo.menu.productGroupsInfo.map(
          productGroupsInfoResponse => ConverterService.convertProductGroupInfoResponse(productGroupsInfoResponse)
        ),
        products: organizationInfo.menu.products.map((productResponse) => {
          return ConverterService.convertProductResponse(productResponse, organizationInfo.menu.sizes, organizationInfo.menu.products)
        }),
        sizes: organizationInfo.menu.sizes
      },
      language: organizationInfo.language,
      timeZone: organizationInfo.timeZone,
      userAgreementUrl: organizationInfo.userAgreementUrl,
      isSupportTakeaway,
      isSupportDelivery,
      isSupportDeliveryZones,
      isQRMenu: !(isSupportDelivery || isSupportTakeaway),
      styles: {
        logo: organizationInfo.logo ?? '',
        mainColor: organizationInfo.mainColor ?? '',
        secondColor: organizationInfo.secondColor ?? '',
        thirdColor: organizationInfo.thirdColor ?? ''
      },
      aboutCompany: organizationInfo.aboutCompany,
      confidentialPolicy: organizationInfo.confidentialPolicy,
      metadata: this.converterService.convertOrganizationMetadata(organizationInfo.metadata),
    };
  }

  public convertDeliveryTerminalResultToDeliveryTerminal(deliveryTerminalResult: IDeliveryTerminalInfo): IDeliveryTerminalInfo {
    const paymentTypes = deliveryTerminalResult.paymentTypes ? deliveryTerminalResult.paymentTypes.map(
      paymentType => this.convertPaymentTypeResponseToPaymentType(paymentType)
    ) : [];
    return {
      id: deliveryTerminalResult.id,
      name: deliveryTerminalResult.name,
      house: deliveryTerminalResult.house,
      region: deliveryTerminalResult.region,
      city: deliveryTerminalResult.city,
      street: deliveryTerminalResult.street,
      country: deliveryTerminalResult.country,
      description: deliveryTerminalResult.description,
      // мы не сможем принять заказ, если нет вариантов для оплаты
      isSupportDelivery: paymentTypes.length > 0 && deliveryTerminalResult.isSupportDelivery,
      isSupportTakeaway: paymentTypes.length > 0 && deliveryTerminalResult.isSupportDelivery,
      latitude: deliveryTerminalResult.latitude,
      longitude: deliveryTerminalResult.longitude,
      phone: deliveryTerminalResult.phone,
      deliveryZones: deliveryTerminalResult.deliveryZones ?? [],
      externalId: deliveryTerminalResult.externalId,
      paymentTypes,
      cookingTimeInMinutes: deliveryTerminalResult.cookingTimeInMinutes ?? 60,
      deliveryPoint: {
        apartment: '',
        latitude: deliveryTerminalResult.latitude,
        longitude: deliveryTerminalResult.longitude,
        country: deliveryTerminalResult.country,
        description: deliveryTerminalResult.description,
        house: deliveryTerminalResult.house,
        region: deliveryTerminalResult.region,
        city: deliveryTerminalResult.city,
        street: deliveryTerminalResult.street
      }
    };
  }

  public convertPaymentTypeResponseToPaymentType(paymentType: ITerminalPaymentType): ITerminalPaymentType {
    return {
      id: paymentType.id,
      name: paymentType.name ?? '',
      type: paymentType.type,
      deliveryType: paymentType.deliveryType,
      sort: paymentType.sort,
      isActive: paymentType.isActive
    };
  }

  async clientCancelOrder(orderId: string): Promise<IRpcResponse<any>> {
    return firstValueFrom(this.rpcCall<IRpcResponse<any>>(
      `${this.baseUrl}/rpc/order`, 'ClientCancelOrder', {
        orderId: orderId
    }));
  }

  private rpcCall<T>(url: string, method: string, params: any = null): Observable<T> {
    let requestParams: IRpcRequest = {
      id: new Date().getTime(),
      jsonrpc: '2.0',
      method,
      params: params ?? {}
    };
    if (!!params) {
      requestParams = {...requestParams, params};
    }

    return this.http.post<T>(url, requestParams, {
      headers: this.headers
    });
  }
}
